import {
  formatPriceWithCurrency,
  getCurrencySymbol,
  getDisplayedPriceFromPlan,
  getPlanFromPeriod,
} from '@/hooks/query/useQueryProducts';
import { useTranslations, type TranslationKeys } from '@/utils/translations';
import {
  PricingPlanCardBase,
  PricingPlanCardCtaButton,
  type PricingPlanCardCtaVariant,
  type PricingPlanCardFeature,
} from '@heidi/commerce';
import { Period, type ProductView } from '@heidi/commerce/contracts';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { TypographyCaption } from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { memo, useMemo, type ReactNode } from 'react';

interface PricingPlanCardProps {
  tier: SubscriptionTier;
  period: Period;
  product?: ProductView;
  isCurrentPlan?: boolean;
  isMostPopular?: boolean;
  features: PricingPlanCardFeature[];
  featuresSectionTitle?: TranslationKeys;
  ctaText: TranslationKeys;
  onCtaClick: () => void;
  isCtaLoading?: boolean;
  /** When true, CTA button is disabled (e.g. team is not Stripe managed). */
  isCtaDisabled?: boolean;
  /** Tooltip shown when CTA is disabled (e.g. "Contact Sales to upgrade"). */
  ctaDisabledTooltip?: string;
  seeMoreHref?: string;
}

function PricingPlanCardComponent({
  tier,
  period,
  product,
  isCurrentPlan,
  isMostPopular,
  features,
  featuresSectionTitle,
  ctaText,
  onCtaClick,
  isCtaLoading,
  isCtaDisabled,
  ctaDisabledTooltip,
  seeMoreHref,
}: PricingPlanCardProps) {
  const { t, locale } = useTranslations();

  const plan = getPlanFromPeriod(period, product);
  const currencyCode = plan?.currency ?? '';
  const currencySymbol = getCurrencySymbol(currencyCode);
  const freePrice = formatPriceWithCurrency('0', currencyCode, currencySymbol);

  const displayPrice =
    tier === SubscriptionTier.FREE
      ? freePrice
      : getDisplayedPriceFromPlan(plan, 'month', false, locale);

  const { ctaVariant, ctaIcon } = useMemo((): {
    ctaVariant: PricingPlanCardCtaVariant;
    ctaIcon?: ReactNode;
  } => {
    if (!isCurrentPlan) return { ctaVariant: 'secondary' };

    return tier === SubscriptionTier.CLINICIAN_PRO_PLUS ||
      tier === SubscriptionTier.PRO_PLUS
      ? { ctaVariant: 'pro_plus', ctaIcon: <TierPracticeFilled size={16} /> }
      : { ctaVariant: 'pro', ctaIcon: <TierProFilled size={16} /> };
  }, [isCurrentPlan, tier]);

  const priceSubtitles =
    tier !== SubscriptionTier.FREE && plan ? (
      <div className="flex flex-col items-start">
        <TypographyCaption className="text-text-secondary">
          {t('pricingPlansModal.perUserMonth' as TranslationKeys)}
        </TypographyCaption>
        <TypographyCaption className="text-text-secondary">
          {period === Period.ONE_YEAR
            ? t('pricingPlansModal.billedAnnually' as TranslationKeys)
            : t('pricingPlansModal.billedMonthly' as TranslationKeys)}
        </TypographyCaption>
      </div>
    ) : undefined;

  return (
    <PricingPlanCardBase
      className="size-full min-h-0 min-w-0"
      isCurrentPlan={isCurrentPlan}
      isMostPopular={isMostPopular}
      isClinicianProPlusTier={tier === SubscriptionTier.CLINICIAN_PRO_PLUS}
      planTitle={t(
        `pricingPlansModal.planNames.${tier.toLowerCase()}` as TranslationKeys
      )}
      priceText={displayPrice ?? '—'}
      priceSubtitles={priceSubtitles}
      cta={
        <PricingPlanCardCtaButton
          variant={ctaVariant}
          icon={ctaIcon}
          onClick={onCtaClick}
          isCtaLoading={isCtaLoading}
          isCtaDisabled={isCtaDisabled}
          ctaText={t(ctaText)}
        />
      }
      isCtaDisabled={isCtaDisabled}
      ctaDisabledTooltip={ctaDisabledTooltip}
      features={features}
      featuresSectionTitle={featuresSectionTitle ? t(featuresSectionTitle) : undefined}
      seeMoreHref={seeMoreHref}
    />
  );
}

export const PricingPlanCard = memo(PricingPlanCardComponent);
